import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { EligibleResultView } from '../../api/endpoints.js';
import { ActionButton, CheckboxField } from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { isolateRuns, run } from '../settings/report-template-parts.js';
import { PersonAt, bilingual } from './report-parts.js';

/**
 * Choosing the approved results a report prints, and the order it prints them.
 *
 * ===========================================================================
 * ONLY WHAT THE SERVER OFFERED, AND NOTHING ABOUT WHAT A RESULT SAYS.
 * ===========================================================================
 *
 * The list is the server's list of APPROVED, LOCKED results for one work order
 * (D124). A result already printed in another live report is shown — hiding it
 * would leave the preparer wondering where it went — but it cannot be chosen,
 * and it names the report that holds it. The server refuses it anyway.
 *
 * A row carries the test's name, the sample number and who approved it. It
 * never carries a reading, a unit, a value or a verdict: that is the record's
 * content, and it is printed verbatim from the record, not chosen here.
 */
export function ResultSelection({
  eligible,
  selected,
  onChange,
  currentReportId,
  error,
}: {
  readonly eligible: readonly EligibleResultView[];
  /** Attempt ids, in print order. */
  readonly selected: readonly string[];
  readonly onChange: (attemptIds: string[]) => void;
  /** When editing a draft, results already in THIS report stay selectable. */
  readonly currentReportId?: string;
  /** Already translated. */
  readonly error?: string;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const eligibleHeading = useId();
  const orderHeading = useId();

  const heldElsewhere = (result: EligibleResultView) =>
    result.alreadyReportedIn !== null && result.alreadyReportedIn.reportId !== currentReportId;

  const selectable = eligible.filter((result) => !heldElsewhere(result));
  const byId = new Map(eligible.map((result) => [result.attemptId, result]));
  // Only ids the server offered, in the chosen order.
  const ordered = selected.filter((id) => byId.has(id));
  const anyHeldElsewhere = eligible.some(heldElsewhere);

  const toggle = (attemptId: string, include: boolean) => {
    if (include) {
      if (!ordered.includes(attemptId)) onChange([...ordered, attemptId]);
    } else {
      onChange(ordered.filter((id) => id !== attemptId));
    }
  };

  const move = (index: number, offset: -1 | 1) => {
    const target = index + offset;
    if (target < 0 || target >= ordered.length) return;
    const next = [...ordered];
    const [moved] = next.splice(index, 1);
    if (moved !== undefined) next.splice(target, 0, moved);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-gutter">
      <section className="flex flex-col gap-stack" aria-labelledby={eligibleHeading}>
        <div>
          <h3 id={eligibleHeading} className="text-body font-medium text-default-900">
            {t('reports.prepare.eligible')}
          </h3>
          <p className="mt-1 text-caption text-default-600">{t('reports.prepare.eligibleHint')}</p>
        </div>

        {eligible.length === 0 ? (
          <p className="text-body text-default-600">{t('reports.prepare.noneEligible')}</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-inline">
              <ActionButton
                priority="tertiary"
                size="sm"
                isDisabled={selectable.length === 0}
                onPress={() =>
                  onChange([
                    ...ordered,
                    ...selectable
                      .map((result) => result.attemptId)
                      .filter((id) => !ordered.includes(id)),
                  ])
                }
              >
                {t('reports.prepare.selectAll')}
              </ActionButton>
              <ActionButton
                priority="tertiary"
                size="sm"
                isDisabled={ordered.length === 0}
                onPress={() => onChange([])}
              >
                {t('reports.prepare.clearSelection')}
              </ActionButton>
              <span className="text-caption text-default-700" aria-live="polite">
                {t('reports.prepare.selectionCount', { count: ordered.length })}
              </span>
            </div>

            <ul className="flex flex-col divide-y divide-default-100" data-testid="eligible-results">
              {eligible.map((result) => {
                const held = heldElsewhere(result);
                const name = bilingual(result.test.name, locale) ?? t('common.notRecorded');
                return (
                  <li
                    key={result.attemptId}
                    data-attempt={result.attemptId}
                    className="flex flex-col gap-1 py-2.5 first:pt-0 last:pb-0"
                  >
                    <CheckboxField
                      label={name}
                      value={ordered.includes(result.attemptId)}
                      isDisabled={held}
                      onChange={(include) => toggle(result.attemptId, include)}
                    />
                    <p className="text-caption text-default-600">
                      <span className="measure" dir="ltr">
                        {result.sample.number}
                      </span>
                      {' · '}
                      <PersonAt person={result.approvedBy} at={result.approvedAt} />
                    </p>
                    {held && result.alreadyReportedIn ? (
                      <p className="text-caption font-medium text-default-800">
                        {result.alreadyReportedIn.reportNumber
                          ? isolateRuns(
                              t('reports.prepare.alreadyReported', { number: run('number') }),
                              {
                                number: (
                                  <span className="measure" dir="ltr">
                                    {result.alreadyReportedIn.reportNumber}
                                  </span>
                                ),
                              },
                            )
                          : t('reports.prepare.alreadyReportedDraft')}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {anyHeldElsewhere ? (
              <p className="text-caption text-default-600">
                {t('reports.prepare.alreadyReportedHint')}
              </p>
            ) : null}
          </>
        )}
        {error ? (
          <p role="alert" className="text-caption text-danger-700">
            {error}
          </p>
        ) : null}
      </section>

      {ordered.length > 0 ? (
        <section className="flex flex-col gap-stack" aria-labelledby={orderHeading}>
          <div>
            <h3 id={orderHeading} className="text-body font-medium text-default-900">
              {t('reports.prepare.printOrder')}
            </h3>
            <p className="mt-1 text-caption text-default-600">
              {t('reports.prepare.printOrderHint')}
            </p>
          </div>
          <ol className="flex flex-col gap-inline" data-testid="print-order">
            {ordered.map((attemptId, index) => {
              const result = byId.get(attemptId);
              if (!result) return null;
              return (
                <li
                  key={attemptId}
                  data-attempt={attemptId}
                  className="flex flex-wrap items-center gap-inline rounded-medium border border-default-200 px-3 py-2"
                >
                  <span className="tabular-nums text-default-600">{index + 1}</span>
                  <span className="min-w-0 flex-1">
                    <bdi className="text-default-900">
                      {bilingual(result.test.name, locale) ?? t('common.notRecorded')}
                    </bdi>
                    {' · '}
                    <span className="measure text-caption text-default-600" dir="ltr">
                      {result.sample.number}
                    </span>
                  </span>
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    isDisabled={index === 0}
                    onPress={() => move(index, -1)}
                  >
                    {t('reports.prepare.moveUp')}
                  </ActionButton>
                  <ActionButton
                    priority="tertiary"
                    size="sm"
                    isDisabled={index === ordered.length - 1}
                    onPress={() => move(index, 1)}
                  >
                    {t('reports.prepare.moveDown')}
                  </ActionButton>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}
    </div>
  );
}
